import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import {
  Wifi, Radio, Cpu, Battery, Signal, Plus, RefreshCw, Settings,
  AlertTriangle, Check, X, ChevronRight, Truck, MapPin, Gauge,
  Thermometer, Fuel, Activity, Eye, Edit, Trash2, Power } from
'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { useFleetAPI, useVehicles } from '../hooks/useFleetData';

const API_URL = API_BASE;

const TelematicsPage = () => {const { t } = useTranslation("fleet");
  const [activeTab, setActiveTab] = useState('devices');
  const [devices, setDevices] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);

  // Form state
  const [newDevice, setNewDevice] = useState({
    device_type: 'OBD2',
    manufacturer: '',
    model: '',
    serial_number: '',
    vehicle_id: '',
    sim_iccid: '',
    imei: '',
    reporting_interval_seconds: 30
  });

  const { vehicles } = useVehicles({});
  const { fetchAPI } = useFleetAPI();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [devicesData, alertsData] = await Promise.all([
      fetchAPI('/telematics/devices'),
      fetchAPI('/telematics/alerts?limit=50')]
      );
      setDevices(devicesData || []);
      setAlerts(alertsData || []);
    } catch (err) {
      console.error('Failed to load telematics data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadReadings = async (deviceId) => {
    try {
      const data = await fetchAPI(`/telematics/readings?device_id=${deviceId}&limit=50`);
      setReadings(data || []);
    } catch (err) {
      console.error('Failed to load readings:', err);
    }
  };

  const createDevice = async () => {
    try {
      await fetchAPI('/telematics/devices', {
        method: 'POST',
        body: JSON.stringify(newDevice)
      });
      setShowAddModal(false);
      setNewDevice({
        device_type: 'OBD2',
        manufacturer: '',
        model: '',
        serial_number: '',
        vehicle_id: '',
        sim_iccid: '',
        imei: '',
        reporting_interval_seconds: 30
      });
      loadData();
    } catch (err) {
      console.error('Failed to create device:', err);
      alert('Failed to create device');
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      await fetchAPI(`/telematics/alerts/${alertId}/acknowledge`, { method: 'PUT' });
      loadData();
    } catch (err) {
      console.error('Failed to acknowledge alert:', err);
    }
  };

  const deviceTypeIcons = {
    OBD2: Cpu,
    GPS_TRACKER: MapPin,
    DASHCAM: Eye,
    FUEL_SENSOR: Fuel,
    TEMPERATURE_SENSOR: Thermometer,
    TPMS: Gauge,
    COMBO: Activity
  };

  const statusColors = {
    ACTIVE: 'bg-green-100 text-green-700',
    INACTIVE: 'bg-gray-100 text-gray-700',
    OFFLINE: 'bg-red-100 text-red-700',
    MAINTENANCE: 'bg-amber-100 text-amber-700',
    FAULTY: 'bg-red-100 text-red-700'
  };

  const severityColors = {
    INFO: 'bg-blue-100 text-blue-700',
    WARNING: 'bg-amber-100 text-amber-700',
    CRITICAL: 'bg-red-100 text-red-700'
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("telematicsIntegration", "Telematics Integration")}</h1>
          <p className="text-gray-500">{t("manageTelematicsDevices", "Manage telematics devices and real-time vehicle data")}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />{t("refresh", "Refresh")}

          </Button>
          <Button className="bg-red-500 hover:bg-red-600" onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4 mr-2" />{t("addDevice", "Add Device")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t("totalDevices", "Total Devices")}</p>
                <p className="text-2xl font-bold">{devices.length}</p>
              </div>
              <Radio className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t("active", "Active")}</p>
                <p className="text-2xl font-bold text-green-600">
                  {devices.filter((d) => d.status === 'ACTIVE').length}
                </p>
              </div>
              <Wifi className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t("offline", "Offline")}</p>
                <p className="text-2xl font-bold text-red-600">
                  {devices.filter((d) => d.status === 'OFFLINE').length}
                </p>
              </div>
              <Power className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t("activeAlerts", "Active Alerts")}</p>
                <p className="text-2xl font-bold text-amber-600">
                  {alerts.filter((a) => a.status === 'ACTIVE').length}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="devices">{t("devices", "Devices")}</TabsTrigger>
          <TabsTrigger value="alerts">{t("alerts", "Alerts")}</TabsTrigger>
          <TabsTrigger value="readings">{t("liveData", "Live Data")}</TabsTrigger>
        </TabsList>

        {/* Devices Tab */}
        <TabsContent value="devices">
          <Card>
            <CardHeader>
              <CardTitle>{t("registeredDevices", "Registered Devices")}</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ?
              <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                </div> :
              devices.length === 0 ?
              <div className="text-center py-12">
                  <Radio className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{t("noDevicesRegistered", "No Devices Registered")}</h3>
                  <p className="text-gray-500 mb-4">{t("addYourFirst", "Add your first telematics device to start tracking")}</p>
                  <Button onClick={() => setShowAddModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />{t("addDevice", "Add Device")}

                </Button>
                </div> :

              <div className="grid gap-4">
                  {devices.map((device) => {
                  const IconComponent = deviceTypeIcons[device.device_type] || Cpu;
                  return (
                    <div
                      key={device.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer"
                      onClick={() => {
                        setSelectedDevice(device);
                        loadReadings(device.id);
                        setActiveTab('readings');
                      }}>
                      
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        device.status === 'ACTIVE' ? 'bg-green-100' : 'bg-gray-100'}`
                        }>
                            <IconComponent className={`w-6 h-6 ${
                          device.status === 'ACTIVE' ? 'text-green-600' : 'text-gray-600'}`
                          } />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{device.device_code}</p>
                            <p className="text-sm text-gray-500">
                              {device.manufacturer} {device.model} • {device.device_type}
                            </p>
                            <p className="text-xs text-gray-400">{t("sn", "S/N:")}{device.serial_number}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {device.vehicle_code &&
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Truck className="w-4 h-4" />
                              {device.vehicle_code}
                            </div>
                        }
                          <div className="flex items-center gap-2">
                            {device.battery_level &&
                          <div className="flex items-center gap-1 text-sm">
                                <Battery className={`w-4 h-4 ${
                            device.battery_level > 50 ? 'text-green-500' :
                            device.battery_level > 20 ? 'text-amber-500' : 'text-red-500'}`
                            } />
                                {device.battery_level}%
                              </div>
                          }
                            {device.signal_strength &&
                          <div className="flex items-center gap-1 text-sm">
                                <Signal className="w-4 h-4 text-gray-400" />
                                {device.signal_strength}%
                              </div>
                          }
                          </div>
                          <Badge className={statusColors[device.status] || 'bg-gray-100'}>
                            {device.status}
                          </Badge>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </div>);

                })}
                </div>
              }
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts">
          <Card>
            <CardHeader>
              <CardTitle>{t("telematicsAlerts", "Telematics Alerts")}</CardTitle>
            </CardHeader>
            <CardContent>
              {alerts.length === 0 ?
              <div className="text-center py-12">
                  <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{t("noAlerts", "No Alerts")}</h3>
                  <p className="text-gray-500">{t("allSystemsOperating", "All systems operating normally")}</p>
                </div> :

              <div className="space-y-3">
                  {alerts.map((alert) =>
                <div key={alert.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    alert.severity === 'CRITICAL' ? 'bg-red-100' :
                    alert.severity === 'WARNING' ? 'bg-amber-100' : 'bg-blue-100'}`
                    }>
                          <AlertTriangle className={`w-5 h-5 ${
                      alert.severity === 'CRITICAL' ? 'text-red-600' :
                      alert.severity === 'WARNING' ? 'text-amber-600' : 'text-blue-600'}`
                      } />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{alert.title}</p>
                          <p className="text-sm text-gray-500">
                            {alert.alert_type.replace('_', ' ')} • {alert.alert_code}
                          </p>
                          {alert.lat && alert.lng &&
                      <p className="text-xs text-gray-400">{t("location", "Location:")}
                        {(alert.lat ?? 0).toFixed(4)}, {(alert.lng ?? 0).toFixed(4)}
                            </p>
                      }
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right text-sm text-gray-500">
                          {alert.created_at ? new Date(alert.created_at).toLocaleString() : ''}
                        </div>
                        <Badge className={severityColors[alert.severity] || 'bg-gray-100'}>
                          {alert.severity}
                        </Badge>
                        {alert.status === 'ACTIVE' &&
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => acknowledgeAlert(alert.id)}>
                      
                            <Check className="w-4 h-4 mr-1" />{t("acknowledge", "Acknowledge")}

                    </Button>
                    }
                        {alert.status === 'ACKNOWLEDGED' &&
                    <Badge className="bg-green-100 text-green-700">{t("acknowledged", "Acknowledged")}

                    </Badge>
                    }
                      </div>
                    </div>
                )}
                </div>
              }
            </CardContent>
          </Card>
        </TabsContent>

        {/* Readings Tab */}
        <TabsContent value="readings">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  {selectedDevice ? `Live Data - ${selectedDevice.device_code}` : 'Live Data'}
                </CardTitle>
                {selectedDevice &&
                <Button variant="outline" size="sm" onClick={() => loadReadings(selectedDevice.id)}>
                    <RefreshCw className="w-4 h-4 mr-2" />{t("refresh", "Refresh")}

                </Button>
                }
              </div>
            </CardHeader>
            <CardContent>
              {!selectedDevice ?
              <div className="text-center py-12">
                  <Activity className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{t("selectADevice", "Select a Device")}</h3>
                  <p className="text-gray-500">{t("chooseADevice", "Choose a device from the Devices tab to view live data")}</p>
                </div> :
              readings.length === 0 ?
              <div className="text-center py-12">
                  <Activity className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{t("noReadings", "No Readings")}</h3>
                  <p className="text-gray-500">{t("noTelemetryData", "No telemetry data received from this device yet")}</p>
                </div> :

              <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2 font-medium text-gray-500">{t("time", "Time")}</th>
                        <th className="text-left py-3 px-2 font-medium text-gray-500">{t("location", "Location")}</th>
                        <th className="text-left py-3 px-2 font-medium text-gray-500">{t("speed", "Speed")}</th>
                        <th className="text-left py-3 px-2 font-medium text-gray-500">{t("rpm", "RPM")}</th>
                        <th className="text-left py-3 px-2 font-medium text-gray-500">{t("fuel", "Fuel")}</th>
                        <th className="text-left py-3 px-2 font-medium text-gray-500">{t("odometer", "Odometer")}</th>
                        <th className="text-left py-3 px-2 font-medium text-gray-500">{t("events", "Events")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {readings.map((r) =>
                    <tr key={r.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-2">
                            {r.recorded_at ? new Date(r.recorded_at).toLocaleTimeString() : '-'}
                          </td>
                          <td className="py-3 px-2">
                            {r.lat && r.lng ? `${(r.lat ?? 0).toFixed(4)}, ${(r.lng ?? 0).toFixed(4)}` : '-'}
                          </td>
                          <td className="py-3 px-2">{r.speed_kmh ? `${r.speed_kmh} km/h` : '-'}</td>
                          <td className="py-3 px-2">{r.engine_rpm || '-'}</td>
                          <td className="py-3 px-2">{r.fuel_level ? `${r.fuel_level}%` : '-'}</td>
                          <td className="py-3 px-2">{r.odometer_km ? `${r.odometer_km.toLocaleString()} km` : '-'}</td>
                          <td className="py-3 px-2">
                            <div className="flex gap-1">
                              {r.harsh_braking && <Badge className="bg-red-100 text-red-700 text-xs">{t("brake", "Brake")}</Badge>}
                              {r.harsh_acceleration && <Badge className="bg-amber-100 text-amber-700 text-xs">{t("accel", "Accel")}</Badge>}
                              {r.over_speed && <Badge className="bg-purple-100 text-purple-700 text-xs">{t("speed", "Speed")}</Badge>}
                            </div>
                          </td>
                        </tr>
                    )}
                    </tbody>
                  </table>
                </div>
              }
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Device Modal */}
      {showAddModal &&
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t("registerNewDevice", "Register New Device")}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">{t("deviceType", "Device Type")}</label>
                <select
                className="w-full mt-1 px-3 py-2 border rounded-md"
                value={newDevice.device_type}
                onChange={(e) => setNewDevice({ ...newDevice, device_type: e.target.value })}>
                
                  <option value="OBD2">{t("obd2Scanner", "OBD2 Scanner")}</option>
                  <option value="GPS_TRACKER">{t("gpsTracker", "GPS Tracker")}</option>
                  <option value="DASHCAM">{t("dashcam", "Dashcam")}</option>
                  <option value="FUEL_SENSOR">{t("fuelSensor", "Fuel Sensor")}</option>
                  <option value="TEMPERATURE_SENSOR">{t("temperatureSensor", "Temperature Sensor")}</option>
                  <option value="TPMS">{t("tirePressureTpms", "Tire Pressure (TPMS)")}</option>
                  <option value="COMBO">{t("comboDevice", "Combo Device")}</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">{t("manufacturer", "Manufacturer")}</label>
                  <Input
                  value={newDevice.manufacturer}
                  onChange={(e) => setNewDevice({ ...newDevice, manufacturer: e.target.value })}
                  placeholder="e.g., Geotab" />
                
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">{t("model", "Model")}</label>
                  <Input
                  value={newDevice.model}
                  onChange={(e) => setNewDevice({ ...newDevice, model: e.target.value })}
                  placeholder="e.g., GO9" />
                
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">{t("serialNumber", "Serial Number *")}</label>
                <Input
                value={newDevice.serial_number}
                onChange={(e) => setNewDevice({ ...newDevice, serial_number: e.target.value })}
                placeholder="Device serial number" />
              
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">{t("assignToVehicle", "Assign to Vehicle")}</label>
                <select
                className="w-full mt-1 px-3 py-2 border rounded-md"
                value={newDevice.vehicle_id}
                onChange={(e) => setNewDevice({ ...newDevice, vehicle_id: e.target.value })}>
                
                  <option value="">{t("unassigned", "-- Unassigned --")}</option>
                  {vehicles.map((v) =>
                <option key={v.id} value={v.id}>{v.vehicle_code} - {v.make} {v.model}</option>
                )}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">{t("simIccid", "SIM ICCID")}</label>
                  <Input
                  value={newDevice.sim_iccid}
                  onChange={(e) => setNewDevice({ ...newDevice, sim_iccid: e.target.value })}
                  placeholder="SIM card ID" />
                
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">{t("imei", "IMEI")}</label>
                  <Input
                  value={newDevice.imei}
                  onChange={(e) => setNewDevice({ ...newDevice, imei: e.target.value })}
                  placeholder="Device IMEI" />
                
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">{t("reportingIntervalSeconds", "Reporting Interval (seconds)")}</label>
                <Input
                type="number"
                value={newDevice.reporting_interval_seconds}
                onChange={(e) => setNewDevice({ ...newDevice, reporting_interval_seconds: parseInt(e.target.value) })} />
              
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowAddModal(false)}>{t("cancel", "Cancel")}</Button>
                <Button className="bg-red-500 hover:bg-red-600" onClick={createDevice}>{t("registerDevice", "Register Device")}

              </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      }
    </div>);

};

export default TelematicsPage;